import type { ApiAddress } from '#/api/types/ApiAddress';
import type { ApiRemoteOptions } from '#/api/types/ApiRemoteOptions';
import type { I18nFn } from '#/i18n/types/I18nFn';
import { getCountryName, getUsStateName } from '#/utils/internationalization';

import { getCityStateString } from './getCityStateString';

type Args = {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  remoteOptions: ApiRemoteOptions;
  address: ApiAddress;
  i18n: I18nFn;
};

export function getWorkLocationSentence({
  listingType,
  remoteOptions,
  address,
  i18n,
}: Args) {
  if (remoteOptions.remoteZone && listingType === 'EVENT') {
    return i18n('Can attend remotely');
  }

  if (remoteOptions.remoteZone === 'WORLD') {
    if (listingType === 'VOLOP') {
      return i18n(
        'Volunteer can be anywhere in {{strong}}the world{{/strong}}',
        { strong: <strong /> },
      );
    }

    return i18n(
      'Work can be performed from anywhere in {{strong}}the world{{/strong}}',
      { strong: <strong /> },
    );
  }

  if (remoteOptions.remoteZone === 'COUNTRY') {
    if (listingType === 'VOLOP') {
      return i18n('Volunteer must be in {{strong}}%(location)s{{/strong}}', {
        location: getCountryName(i18n, remoteOptions.remoteCountry!),
        strong: <strong />,
      });
    }

    return i18n(
      'Work can be performed from anywhere in {{strong}}%(location)s{{/strong}}',
      {
        location: getCountryName(i18n, remoteOptions.remoteCountry!),
        strong: <strong />,
      },
    );
  }

  if (remoteOptions.remoteZone === 'STATE') {
    if (listingType === 'VOLOP') {
      return i18n(
        'Volunteer must be in {{strong}}%(state)s, %(country)s{{/strong}}',
        {
          state: getUsStateName(i18n, remoteOptions.remoteState!),
          country: remoteOptions.remoteCountry!,
          strong: <strong />,
        },
      );
    }

    return i18n(
      'Work must be performed in {{strong}}%(state)s, %(country)s{{/strong}}',
      {
        state: getUsStateName(i18n, remoteOptions.remoteState ?? ''),
        country: remoteOptions.remoteCountry ?? '',
        strong: <strong />,
      },
    );
  }

  if (remoteOptions.remoteZone === 'CITY') {
    if (listingType === 'VOLOP') {
      return i18n(
        'Volunteer must be in or near {{strong}}%(location)s{{/strong}}',
        { location: getCityStateString(i18n, address), strong: <strong /> },
      );
    }

    return i18n(
      'Work must be performed in or near {{strong}}%(location)s{{/strong}}',
      { location: getCityStateString(i18n, address), strong: <strong /> },
    );
  }
}
